import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { ChatOperationResult } from 'flare-chat-core/contracts';
import { aborted, blocked, failed } from 'flare-chat-core/contracts/chat/operation-result';
import type { InteractionPresentationReducer } from '../../projection/composer/contracts';
import { reduceInteractionPresentation } from '../../projection/composer/composerUiProjectionSelector.js';
import { buildPatchQuestionAnswerSubmitPayload, buildPatchQuestionAnswerCancelPayload } from './patch-question-submit.js';

interface PatchAction {
  interactionKey?: string; type?: string; action_key?: string;
  payload?: { patch_question_node?: object; answers?: object[] };
}
interface Payload { content: string; streamOptions: { modeKey: string; payloadExtra: object } }
type Builder = (input: { patchQuestionNode?: object; answers?: object[] }) => Payload | null;
type Result = ChatOperationResult<unknown>;
// The existing JS builders normalize these payloads but infer an empty array as never[].
const builders = new Map<string, Builder>([
  ['submit_patch_question_answer', buildPatchQuestionAnswerSubmitPayload as Builder],
  ['cancel_patch_question_answer', buildPatchQuestionAnswerCancelPayload as Builder],
]);
const matchesScope = (key: string, scopeKey: string) => {
  try { return JSON.parse(key)?.[0] === scopeKey; }
  catch { return false; }
};

/** Own only the existing patch-question command lifecycle, never backend acceptance. */
export const usePatchQuestionSubmission = ({ scopeKey, onSubmit }: {
  scopeKey: string; onSubmit?: (payload: Payload) => Promise<Result>;
}) => {
  const [presentations, dispatch] = useReducer<InteractionPresentationReducer>(reduceInteractionPresentation, new Map());
  // Inactive scopes retain their operation identity; returning must coalesce an in-flight command.
  const pending = useRef(new Map<string, Promise<Result>>());
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const scope = useMemo(() => ({ active: true, scopeKey, pending: pending.current }), [scopeKey]);
  const current = useRef(scope);
  current.current = scope;
  useEffect(() => { scope.active = true; return () => { scope.active = false; }; }, [scope]);
  const submit = useCallback((action: PatchAction): Promise<Result> => {
    if (!scope.active || current.current !== scope) return Promise.resolve(aborted());
    const key = action.interactionKey;
    const build = builders.get(action.type ?? action.action_key ?? '');
    if (!key || !build || !onSubmit) return Promise.resolve(blocked('action_input_invalid'));
    if (!matchesScope(key, scopeKey)) return Promise.resolve(blocked('input_request_stale'));
    const running = scope.pending.get(key);
    if (running) return running;
    const payload = build({ patchQuestionNode: action.payload?.patch_question_node, answers: action.payload?.answers });
    if (!payload) return Promise.resolve(blocked('action_input_invalid'));
    const attempt = {};
    dispatch({ type: 'submit', key, attempt });
    const executing = Promise.resolve().then(() => scope.active && current.current === scope ? onSubmit(payload) : aborted())
      .catch((error) => failed(error, 'submit_patch_question_answer')).then((result) => {
        // Key/attempt fencing isolates the projection even when another scope is visible.
        if (mounted.current) dispatch({ type: 'settle', key, attempt, result });
        return result;
      }).finally(() => { scope.pending.delete(key); });
    scope.pending.set(key, executing);
    return executing;
  }, [scope, scopeKey, onSubmit]);
  return { presentations, submit };
};
